/* The short roue editor — merchant-app review MA-11 (wireframe 2), the walkable set's guide/4: three blocks, « Nom »,
   « Cadeaux et chances » (one row per gift: what it is, its chance as a stepper, an optional monthly cap), « Aperçu et
   coût », with the wheel beside. The guide's step 4 uses it today; Cadeaux › Roues moves to it with R4.
   Controlled: the caller owns the draft (weights in tenths, TENTHS = 100 %) and decides when to save; `lib` is the
   gift library (mergeTypes: API seeds + the merchant's own). */
import type { ReactNode } from 'react'
import { Button, Field, Input } from '@/ui'
import { CoutParParticipation } from './Cout'
import { ROUE_MAX_TYPES, TENTHS, Wheel, designFor, fmtEur, fmtPct, per100,
  roueIssues, tenthsTotal, toReward, type GiftType, type Roue } from '@/lib/wheel'

const STEP = 50 // 5 %, in tenths
/** Equal shares in whole percents, the first gift taking the rest (the walkable set's « Équilibrer », also on add and remove). */
export function evenShares(r: Roue): Roue {
  const live = r.rewards.filter(x => !x.excluded)
  if (!live.length) return r
  const base = Math.floor(100 / live.length), first = live[0].rewardId
  return { ...r, rewards: r.rewards.map(x => x.excluded ? x : { ...x, weight: (base + (x.rewardId === first ? 100 - base * live.length : 0)) * 10 }) }
}
const validityLabel = (d: number) => d === 180 ? '6 mois' : d === 365 ? '1 an' : `${d} jours`

/** What stops a roue being kept: the RW-12 rules, and the chances adding up to 100 %. */
export function roueBlockers(r: Roue): string[] {
  const total = tenthsTotal(r)
  return [...(total !== TENTHS ? [`Les chances font ${fmtPct(total / 10)} : il faut 100 %. « Équilibrer » le fait pour vous.`] : []), ...roueIssues(r)]
}

export default function RoueCourte({ draft, setDraft, onNewGift, head, lib }: { draft: Roue; setDraft: (r: Roue) => void; onNewGift: () => void; head?: ReactNode; lib: GiftType[] }) {
  const total = tenthsTotal(draft)
  const free = lib.filter(t => !draft.rewards.some(x => x.rewardId === t.id))
  const rows = draft.rewards.filter(x => !x.excluded)
  const upd = (id: string, patch: Partial<Roue['rewards'][number]>) => setDraft({ ...draft, rewards: draft.rewards.map(x => x.rewardId === id ? { ...x, ...patch } : x) })
  const step = (id: string, d: number) => { const x = draft.rewards.find(y => y.rewardId === id)!; upd(id, { weight: Math.max(0, Math.min(TENTHS, x.weight + d)) }) }
  /* a gift added or taken off: equal shares again, so the total stays 100 % (as the walkable set does) */
  const remove = (id: string) => setDraft(evenShares({ ...draft, rewards: draft.rewards.filter(x => x.rewardId !== id) }))
  const add = (id: string) => { const t = lib.find(x => x.id === id); if (t) setDraft(evenShares({ ...draft, rewards: [...draft.rewards, toReward(t, 0)] })) }
  const blockers = roueBlockers(draft)

  return (
    <div className="cols-2-1">
      <div style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
        {head}
        {draft.usedBy.length > 0 && <p className="f-help" style={{ margin: '-8px 0 0' }}>Utilisée par {draft.usedBy.map(u => `« ${u} »`).join(', ')}. Les clients en cours gardent leur version.</p>}
        <section className="card">
          <Field label="Nom de la roue (pour vous)">{id => <Input id={id} value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} />}</Field>
        </section>
        <section className="card" data-testid="chances">
          <p className="f-label" style={{ margin: '0 0 6px' }}>Cadeaux et chances</p>
          {rows.map(x => {
            const t = lib.find(y => y.id === x.rewardId)
            return (
              <div key={x.rewardId} data-testid="chance" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 10, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--bmr-border)' }}>
                <span style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <b>{x.name}</b><br />
                  <span className="f-help">valable {validityLabel(t?.validityDays || 60)} · {x.cost ? fmtEur(x.cost) : 'sans coût'}{x.cap ? <> · au plus <input aria-label={`Plafond par mois, ${x.name}`} inputMode="numeric" value={x.cap} onChange={e => upd(x.rewardId, { cap: Math.max(1, parseInt(e.target.value, 10) || 1) })} style={{ width: 56, padding: '2px 6px', border: '1px solid var(--bmr-border)', borderRadius: 6, font: 'inherit' }} /> par mois</> : null}</span><br />
                  <a href="#" className="f-help" onClick={e => { e.preventDefault(); upd(x.rewardId, { cap: x.cap ? null : 20 }) }}>{x.cap ? 'Retirer le plafond' : 'Ajouter un plafond par mois'}</a>
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
                  <Button size="sm" aria-label={`Moins de chances, ${x.name}`} onClick={() => step(x.rewardId, -STEP)}>−</Button>
                  <b style={{ minWidth: 56, textAlign: 'center' }} data-testid="pct">{fmtPct(x.weight / 10)}</b>
                  <Button size="sm" aria-label={`Plus de chances, ${x.name}`} onClick={() => step(x.rewardId, STEP)}>+</Button>
                  {rows.length > 1 && <Button size="sm" variant="quiet" aria-label={`Retirer ${x.name}`} onClick={() => remove(x.rewardId)}>×</Button>}
                </span>
              </div>
            )
          })}
          {draft.rewards.filter(x => x.excluded).map(x => <p key={x.rewardId} className="f-help" data-testid="exclu" style={{ margin: '10px 0 0' }}><b>{x.name}</b>&nbsp;: exclu du prochain tirage · {x.excluded}.</p>)}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
            <b data-testid="somme" style={{ color: total === TENTHS ? 'var(--bmr-action)' : 'var(--bmr-warn)' }}>{total === TENTHS ? '= 100 %' : `= ${fmtPct(total / 10)} : il faut 100 %`}</b>
            <Button size="sm" variant="quiet" onClick={() => setDraft(evenShares(draft))}>Équilibrer</Button>
          </div>
          {draft.rewards.length < ROUE_MAX_TYPES
            ? <div style={{ marginTop: 12 }}>
                <p className="f-label" style={{ margin: '0 0 6px' }}>Ajouter un cadeau</p>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {free.map(t => <Button key={t.id} size="sm" onClick={() => add(t.id)}>+ {t.name}</Button>)}
                  <Button size="sm" variant="quiet" onClick={onNewGift}>+ Un nouveau cadeau…</Button>
                </div>
              </div>
            : <p className="f-help" style={{ margin: '12px 0 0' }}>Douze cadeaux au plus sur une roue.</p>}
        </section>
        <section className="card" data-testid="cout">
          <p className="f-label" style={{ margin: '0 0 6px' }}>Aperçu et coût</p>
          <p className="t-body" style={{ margin: 0 }}><b><CoutParParticipation roue={draft} /></b></p>
          <p className="f-help" style={{ margin: '4px 0 0' }}>Sur 100 participations&nbsp;: {per100(draft)}.</p>
        </section>
        {blockers.length > 0 && <p className="f-help err" style={{ margin: 0 }} data-testid="avant">{blockers.join(' ')}</p>}
      </div>
      <aside aria-label="Ce que voit le client">
        <p className="t-eyebrow">Ce que voit le client</p>
        <div style={{ display: 'grid', justifyItems: 'center', gap: 8 }}>
          <Wheel design={designFor(draft)} size={220} />
          <p className="f-help" style={{ margin: 0, textAlign: 'center' }}>La roue s’arrête sur le cadeau tiré. Les chances sont celles du tableau.</p>
        </div>
      </aside>
    </div>
  )
}
